import { Box, Chip, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import api from "services/api";
import { useStyles } from "./styles";

interface OptantDecisionRecord {
  ID: string;
  PERSON_TAX_PAYER_ID: string;
  EMPLOYEE_TAX_PAYER_ID: string;
  BENEFICIARY_TYPE: "TITULAR" | "DEPENDENT";
  IS_OPTANT: boolean;
  SOURCE: string;
  EFFECTIVE_DATE: string;
  CREATED_AT: string;
  CREATED_BY: string;
  REASON: string | null;
}

const SOURCE_LABEL: Record<string, string> = {
  COMPULSORY_AUTO_OPT_IN: "Inclusão automática",
  LIVRE_ADESAO_OPT_IN: "Adesão — optou",
  LIVRE_ADESAO_OPT_OUT: "Adesão — recusou",
  EXCEPTION_APP_OPT_IN: "Exceção — incluído",
  EXCEPTION_APP_OPT_OUT: "Exceção — removido",
  EXCEPTION_APP_PLAN_CHANGE: "Exceção — troca de plano",
  MIGRATION_OPT_IN: "Migração",
  CASCADE_NOT_ENDERECAVEL: "Sem endereçabilidade",
  CASCADE_FROM_TITULAR: "Herdado do titular",
  DEPENDENT_INHERITED: "Herdado do titular",
};

function formatCpf(cpf: string) {
  // CPF column comes back as 11 digits; pad defensively so a malformed value
  // doesn't silently render unmasked.
  const padded = (cpf ?? "").padStart(11, "0");
  return padded.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

function formatDate(iso: string) {
  // EFFECTIVE_DATE is a calendar date (UTC midnight from Snowflake);
  // toLocaleDateString without timeZone shifts to the browser's offset and
  // can flip the day in pt-BR (UTC-3).
  return iso ? new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—";
}

export function ExcecaoHistorico() {
  const { workspaceActivityId } = useParams() as { workspaceActivityId: string };
  const classes = useStyles();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["excecao-history", workspaceActivityId],
    queryFn: () =>
      api
        .get(`/v2/apps/exception/${workspaceActivityId}/history`)
        .then((r) => r.data.data as OptantDecisionRecord[]),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <span style={{ color: "#C62828", fontSize: "0.9rem" }}>
          Não foi possível carregar o histórico
        </span>
      </Box>
    );
  }

  if (!data?.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <span style={{ color: "#888", fontSize: "0.9rem" }}>
          Nenhuma decisão registrada para este contrato
        </span>
      </Box>
    );
  }

  return (
    <table className={classes.tableBase}>
      <thead className={classes.tableHeader}>
        <tr>
          <th className={classes.tableOthersSize}>CPF</th>
          <th className={classes.tableOthersSize}>Tipo</th>
          <th className={classes.tableOthersSize}>Decisão</th>
          <th className={classes.tableOthersSize}>Origem</th>
          <th className={classes.tableOthersSize}>Data efetiva</th>
          <th className={classes.tableOthersSize}>Registrado em</th>
          <th className={classes.tableOthersSize}>Motivo</th>
        </tr>
      </thead>
      <tbody className={classes.tableBody}>
        {data.map((row) => (
          <tr key={row.ID}>
            <td className={classes.tableOthersSize}>{formatCpf(row.PERSON_TAX_PAYER_ID)}</td>
            <td className={classes.tableOthersSize}>
              {row.BENEFICIARY_TYPE === "TITULAR" ? "Titular" : "Dependente"}
            </td>
            <td className={classes.tableOthersSize}>
              <Chip
                label={row.IS_OPTANT ? "Optante" : "Não optante"}
                size="small"
                sx={{
                  backgroundColor: row.IS_OPTANT ? "#E8F5E9" : "#FFEBEE",
                  color: row.IS_OPTANT ? "#2E7D32" : "#C62828",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            </td>
            <td className={classes.tableOthersSize}>
              {SOURCE_LABEL[row.SOURCE] ?? row.SOURCE}
            </td>
            <td className={classes.tableOthersSize}>{formatDate(row.EFFECTIVE_DATE)}</td>
            <td className={classes.tableOthersSize}>{formatDate(row.CREATED_AT)}</td>
            <td className={classes.tableOthersSize}>{row.REASON ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
